import React, { useState, useEffect } from 'react';
import './OverviewPage.css';
import PieChart from './charts/PieChart';
import StackedAreaChart from './charts/StackedAreaChart';
import LineChartWithLegend from './charts/LineChartWithLegend';
import ChartModal from './ChartModal';
import ChartLogo from './ChartLogo';
import companiesData from '../data/companies.json';
import { useGlobalData } from '../context/GlobalDataContext';
import { submitTicker } from '../services/dataService';

const OverviewPage = ({ onNavigateToAsset, onNavigateToCompany }) => {
  const [modalChart, setModalChart] = useState(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [tickerModalOpen, setTickerModalOpen] = useState(false);
  const [tickerInput, setTickerInput] = useState('');
  const [tickerSubmitting, setTickerSubmitting] = useState(false);
  const [tickerSubmitError, setTickerSubmitError] = useState(null);
  const [datLeaders, setDATLeaders] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  
  // Use global data context
  const { 
    getDATLeadersData,
    getMarketCapLeaderboard,
    getPremiumLeaderboard,
    getTimeSeriesByAsset,
    loading: globalDataLoading,
    loadingProgress,
    loadingStartTime,
    latestSnapshotData,
    // Overview aggregates
    totalMarketCap,
    cryptoHoldings,
    supplyPercentagesData,
    supplyPercentagesByAsset,
    navGrowthByAssetData
  } = useGlobalData();

  // Calculate adjusted loading progress - if loading for >=3.5 seconds, show min 45%
  // Use state that updates periodically to check elapsed time
  const [adjustedLoadingProgress, setAdjustedLoadingProgress] = useState(0);
  
  useEffect(() => {
    if (!loadingStartTime) {
      setAdjustedLoadingProgress(loadingProgress || 0);
      return;
    }
    
    // Update immediately
    const updateProgress = () => {
      const currentProgress = loadingProgress || 0;
      const elapsedSeconds = (Date.now() - loadingStartTime) / 1000;
      
      if (elapsedSeconds >= 3.5) {
        setAdjustedLoadingProgress(Math.max(45, currentProgress));
      } else {
        setAdjustedLoadingProgress(currentProgress);
      }
    };
    
    updateProgress();
    
    // Update every 100ms to check if 3.5 seconds have passed
    const interval = setInterval(updateProgress, 100);
    
    return () => clearInterval(interval);
  }, [loadingProgress, loadingStartTime]);

  // Calculate total distinct tickers across all assets
  const getTotalTickers = () => {
    const allCompanies = [
      ...companiesData.bitcoin.filter(c => c.trackData === true),
      ...companiesData.ethereum.filter(c => c.trackData === true),
      ...companiesData.solana.filter(c => c.trackData === true)
    ];
    
    // Get unique tickers
    const uniqueTickers = [...new Set(allCompanies.map(company => company.ticker))];
    return uniqueTickers.length;
  };

  // Note: Market cap and trading volume charts use aggregated growth asset data,
  // so no additional lazy loading is needed here

  // Calculate onchain transparency percentage based on distinct tickers
  const getOnchainTransparencyPercentage = () => {
    const allCompanies = [
      ...companiesData.bitcoin.filter(c => c.trackData === true),
      ...companiesData.ethereum.filter(c => c.trackData === true),
      ...companiesData.solana.filter(c => c.trackData === true)
    ];
    
    // Get unique tickers
    const uniqueTickers = [...new Set(allCompanies.map(company => company.ticker))];
    
    // Count disclosed tickers (if any company with that ticker has disclosed wallets)
    const disclosedTickers = uniqueTickers.filter(ticker => {
      const companiesWithTicker = allCompanies.filter(company => company.ticker === ticker);
      return companiesWithTicker.some(company => company.onchainWalletsDisclosed === "Yes");
    });
    
    if (uniqueTickers.length === 0) return 0;
    return Math.round((disclosedTickers.length / uniqueTickers.length) * 100);
  };

  // Get count of distinct tickers with disclosed wallets
  const getDisclosedTickersCount = () => {
    const allCompanies = [
      ...companiesData.bitcoin.filter(c => c.trackData === true),
      ...companiesData.ethereum.filter(c => c.trackData === true),
      ...companiesData.solana.filter(c => c.trackData === true)
    ];
    
    // Get unique tickers
    const uniqueTickers = [...new Set(allCompanies.map(company => company.ticker))];
    
    // Count disclosed tickers (if any company with that ticker has disclosed wallets)
    const disclosedTickers = uniqueTickers.filter(ticker => {
      const companiesWithTicker = allCompanies.filter(company => company.ticker === ticker);
      return companiesWithTicker.some(company => company.onchainWalletsDisclosed === "Yes");
    });
    
    return disclosedTickers.length;
  };

  // Function to download latest snapshot data as CSV
  const downloadLatestDataAsCSV = () => {
    if (!latestSnapshotData || latestSnapshotData.length === 0) {
      return;
    }

    // Get the headers from the table columns
    const headers = [
      'Ticker',
      'Market Cap Estimated',
      'Crypto Holdings',
      'Asset Tracked',
      'Crypto Holdings USD',
      'Stock Price Close',
      'Trading TX Count',
      'NAV Per Share',
      'NAV USD',
      'Crypto Per Share',
      'Outstanding Shares',
      'mNAV',
      'Premium or Discount'
    ];

    // Map the data fields to match the headers
    const csvRows = [];
    csvRows.push(headers.join(','));

    latestSnapshotData.forEach(row => {
      const values = [
        row.ticker || '',
        row.mcap_estimated || '',
        row.crypto_holdings || '',
        getAssetTracked(row.ticker),
        row.crypto_holdings_usd || '',
        row.stock_price_close || '',
        row.trading_tx_count || '',
        row.nav_per_share || '',
        row.nav_usd || '',
        row.crypto_per_share || '',
        row.outstanding_shares_estimated || '',
        row.mnav || '',
        row.premium_discount !== null && row.premium_discount !== undefined ? `${(row.premium_discount * 100).toFixed(2)}%` : ''
      ];
      csvRows.push(values.join(','));
    });

    const csvString = csvRows.join('\n');
    
    // Get the date from the data
    const bmnrRecord = latestSnapshotData.find(d => d.ticker === 'BMNR');
    const dataDate = bmnrRecord?.series_date;
    let filename = 'latest_day_data';
    if (dataDate) {
      const dateMatch = dataDate.match(/(\d{4})-(\d{2})-(\d{2})/);
      if (dateMatch) {
        const [, year, month, day] = dateMatch;
        filename = `latest_day_data_${year}-${month}-${day}`;
      }
    }

    // Create and trigger download
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Fetch total market cap, crypto holdings data, and individual ticker holdings
  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        const datLeadersData = await getDATLeadersData().catch(() => ({
          tradingVolumeLeader: null,
          transactionCountLeader: null,
          allDATCompanies: []
        }));
        setDATLeaders(datLeadersData);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [getDATLeadersData]);

  const formatMarketCap = (marketCap) => {
    if (!marketCap || marketCap === 0) return 'N/A';
    
    if (marketCap >= 1e12) {
      return '$' + (marketCap / 1e12).toFixed(1) + 'T';
    } else if (marketCap >= 1e9) {
      return '$' + (marketCap / 1e9).toFixed(1) + 'B';
    } else if (marketCap >= 1e6) {
      return '$' + (marketCap / 1e6).toFixed(1) + 'M';
    }
    return '$' + marketCap.toLocaleString();
  };

  const formatUSD = (value) => {
    if (!value || value === 0) return 'N/A';
    
    if (value >= 1e12) {
      return '$' + (value / 1e12).toFixed(1) + 'T';
    } else if (value >= 1e9) {
      return '$' + (value / 1e9).toFixed(1) + 'B';
    } else if (value >= 1e6) {
      return '$' + (value / 1e6).toFixed(1) + 'M';
    }
    return '$' + value.toLocaleString();
  };

  const formatTurnover = (value) => {
    if (!value || value === 0) return 'N/A';
    
    if (value >= 1e12) {
      return '$' + (value / 1e12).toFixed(1) + 'T';
    } else if (value >= 1e9) {
      return '$' + (value / 1e9).toFixed(1) + 'B';
    } else if (value >= 1e6) {
      return '$' + (value / 1e6).toFixed(1) + 'M';
    } else if (value >= 1e3) {
      return '$' + (value / 1e3).toFixed(1) + 'k';
    }
    return '$' + value.toLocaleString();
  };

  const formatNumber = (value) => {
    if (!value || value === 0) return 'N/A';
    
    if (value >= 1e9) {
      return (value / 1e9).toFixed(1) + 'B';
    } else if (value >= 1e6) {
      return (value / 1e6).toFixed(1) + 'M';
    } else if (value >= 1e3) {
      return (value / 1e3).toFixed(1) + 'K';
    }
    return value.toLocaleString();
  };

  const formatCryptoHoldings = (value) => {
    if (value === null || value === undefined) return 'N/A';
    
    const num = parseFloat(value);
    if (!Number.isFinite(num) || num === 0) return 'N/A';
    
    const absNum = Math.abs(num);
    let baseValue = absNum;
    let suffix = '';
    
    if (absNum >= 1e9) {
      baseValue = absNum / 1e9;
      suffix = 'B';
    } else if (absNum >= 1e6) {
      baseValue = absNum / 1e6;
      suffix = 'M';
    } else if (absNum >= 1e3) {
      baseValue = absNum / 1e3;
      suffix = 'K';
    }
    
    const formatted = `${baseValue.toFixed(1)}${suffix}`;
    return num < 0 ? `-${formatted}` : formatted;
  };

  // Sorting functions
  const handleSort = (key) => {
    let direction = 'asc';
    
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = null; // Remove sorting
    }
    
    setSortConfig({ key: direction ? key : null, direction });
  };

  const getSortableData = (data) => {
    if (!data || !sortConfig.key || !sortConfig.direction) {
      return data;
    }

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Special handling for asset_tracked
      if (sortConfig.key === 'asset_tracked') {
        aValue = getAssetTracked(a.ticker);
        bValue = getAssetTracked(b.ticker);
      }

      // Handle null/undefined values
      if (aValue === null || aValue === undefined || aValue === 'N/A') aValue = 0;
      if (bValue === null || bValue === undefined || bValue === 'N/A') bValue = 0;

      // Convert to numbers for numeric columns
      const numericColumns = [
        'mcap_estimated', 'stock_price_close', 'crypto_holdings', 'crypto_holdings_usd',
        'trading_tx_count', 'nav_per_share', 'nav_usd', 'crypto_per_share',
        'outstanding_shares_estimated', 'mnav', 'premium_discount'
      ];
      
      if (numericColumns.includes(sortConfig.key)) {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }

      if (sortConfig.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  const getSortIcon = (columnKey) => {
    // Show arrow for Market Cap Estimated by default, hide for others unless sorted
    if (sortConfig.key !== columnKey) {
      if (columnKey === 'mcap_estimated') {
        return <span className="sort-icon inactive">↕</span>;
      }
      return null; // Hide arrow by default for other columns
    }
    
    if (sortConfig.direction === 'asc') {
      return <span className="sort-icon active">↑</span>;
    } else if (sortConfig.direction === 'desc') {
      return <span className="sort-icon active">↓</span>;
    }
    
    return <span className="sort-icon inactive">↕</span>;
  };

  // Find company name by ticker
  const getCompanyName = (ticker) => {
    const allCompanies = [
      ...(companiesData.bitcoin || []).filter(c => c.trackData === true),
      ...(companiesData.ethereum || []).filter(c => c.trackData === true),
      ...(companiesData.solana || []).filter(c => c.trackData === true)
    ];
    const company = allCompanies.find(c => c.ticker === ticker);
    return company ? company.name : ticker;
  };

  // Get asset tracked by ticker
  const getAssetTracked = (ticker) => {
    if ((companiesData.bitcoin || []).some(c => c.ticker === ticker && c.trackData === true)) {
      return 'BTC';
    }
    if ((companiesData.ethereum || []).some(c => c.ticker === ticker && c.trackData === true)) {
      return 'ETH';
    }
    if ((companiesData.solana || []).some(c => c.ticker === ticker && c.trackData === true)) {
      return 'SOL';
    }
    return 'N/A';
  };

  // Calculate percentage for a ticker within its asset type using supply percentages filtered by asset
  const getTickerPercentage = (assetType, ticker) => {
    const assetPercentages = supplyPercentagesByAsset[assetType];
    if (!assetPercentages || !assetPercentages[ticker]) return 0;
    
    const tickerPercentage = assetPercentages[ticker] || 0;
    const totalPercentage = Object.values(assetPercentages).reduce((sum, pct) => sum + pct, 0);
    
    if (totalPercentage === 0) return 0;
    
    // Return percentage relative to total for this asset
    return (tickerPercentage / totalPercentage) * 100;
  };

  // Get top 3 tickers by percentage plus "Other" for an asset type (filtered by asset column)
  const getTopTickersWithOther = (assetType) => {
    const assetPercentages = supplyPercentagesByAsset[assetType];
    
    if (!assetPercentages || Object.keys(assetPercentages).length === 0) {
      return [];
    }
    
    // Calculate percentages for all tickers (already filtered by asset column)
    const tickerPercentages = Object.keys(assetPercentages)
      .map(ticker => ({
        ticker: ticker,
        percentage: getTickerPercentage(assetType, ticker)
      }))
      .filter(item => item.percentage > 0) // Only include tickers with data
      .sort((a, b) => b.percentage - a.percentage); // Sort descending
    
    // Get top 3
    const top3 = tickerPercentages.slice(0, 3);
    
    // Sum the rest for "Other"
    const otherPercentage = tickerPercentages.slice(3).reduce((sum, item) => sum + item.percentage, 0);
    
    // Build result array
    const result = top3.map(item => ({
      ticker: item.ticker,
      percentage: item.percentage,
      isOther: false
    }));
    
    // Add "Other" if there are more than 3 tickers
    if (otherPercentage > 0) {
      result.push({
        ticker: 'Other',
        percentage: otherPercentage,
        isOther: true
      });
    }
    
    return result;
  };

  // Navigation handlers
  const handleAssetCardClick = (assetType) => {
    if (onNavigateToAsset) {
      onNavigateToAsset(assetType);
    }
  };

  const handleTickerClick = (ticker) => {
    if (onNavigateToCompany) {
      onNavigateToCompany(ticker);
    }
  };

  // Get NAV growth data summed by asset type
  const getNAVGrowthDataByAsset = () => {
    const data = navGrowthByAssetData || [];
    const startDate = '2025-02-20';
    return data.filter(item => item.date >= startDate);
  };

  // Use real supply percentages data or fallback to dummy data
  const startDate = '2025-02-20';
  const timeSeriesDataRaw = supplyPercentagesData && supplyPercentagesData.length > 0 
    ? supplyPercentagesData.map(item => ({
        ...item,
        // Use percentages as-is from database
        btc_supply: item.btc_supply,
        eth_supply: item.eth_supply,
        sol_supply: item.sol_supply,
        // Add dummy data for other charts that still need it
        btc_nav: 15000000, eth_nav: 8000000, sol_nav: 2000000,
        btc_volume: 45, eth_volume: 35, sol_volume: 20,
        btc_mcap: 1200000000, eth_mcap: 800000000, sol_mcap: 200000000
      }))
    : [
        // Fallback dummy data
        { date: '2024-01-01', btc_nav: 15000000, eth_nav: 8000000, sol_nav: 2000000, btc_supply: 2.1, eth_supply: 1.8, sol_supply: 0.5, btc_volume: 45, eth_volume: 35, sol_volume: 20, btc_mcap: 1200000000, eth_mcap: 800000000, sol_mcap: 200000000 },
        { date: '2024-02-01', btc_nav: 18000000, eth_nav: 9500000, sol_nav: 2500000, btc_supply: 2.3, eth_supply: 2.1, sol_supply: 0.6, btc_volume: 50, eth_volume: 30, sol_volume: 20, btc_mcap: 1400000000, eth_mcap: 950000000, sol_mcap: 250000000 },
        { date: '2024-03-01', btc_nav: 22000000, eth_nav: 11000000, sol_nav: 3000000, btc_supply: 2.5, eth_supply: 2.4, sol_supply: 0.7, btc_volume: 55, eth_volume: 28, sol_volume: 17, btc_mcap: 1700000000, eth_mcap: 1100000000, sol_mcap: 300000000 },
        { date: '2024-04-01', btc_nav: 25000000, eth_nav: 12000000, sol_nav: 3500000, btc_supply: 2.7, eth_supply: 2.6, sol_supply: 0.8, btc_volume: 52, eth_volume: 32, sol_volume: 16, btc_mcap: 1950000000, eth_mcap: 1200000000, sol_mcap: 350000000 },
        { date: '2024-05-01', btc_nav: 28000000, eth_nav: 13500000, sol_nav: 4000000, btc_supply: 2.9, eth_supply: 2.8, sol_supply: 0.9, btc_volume: 48, eth_volume: 35, sol_volume: 17, btc_mcap: 2200000000, eth_mcap: 1350000000, sol_mcap: 400000000 }
      ];
  
  // Filter timeSeriesData to start from Feb 20, 2025
  const timeSeriesData = timeSeriesDataRaw.filter(item => item.date >= startDate);

  const handleSubmitTicker = async (e) => {
    e.preventDefault();
    if (!tickerInput.trim()) return;
    
    const ticker = tickerInput.trim().toUpperCase();
    setTickerSubmitting(true);
    setTickerSubmitError(null);
    
    try {
      await submitTicker(ticker);
      alert(`Thank you for submitting ${ticker}! We'll review it for addition to our tracking.`);
      setTickerInput('');
      setTickerModalOpen(false);
    } catch (error) {
      setTickerSubmitError(error.message || 'Failed to submit ticker. Please try again.');
    } finally {
      setTickerSubmitting(false);
    }
  };

  const openModal = () => {
    setTickerModalOpen(true);
  };

  const closeModal = () => {
    setTickerModalOpen(false);
    setTickerInput('');
    setTickerSubmitError(null);
  };

  // Check if we have critical data loaded
  const hasDataLoaded = latestSnapshotData && latestSnapshotData.length > 0 && 
                        totalMarketCap !== null && 
                        cryptoHoldings !== null;

  return (
    <div className="overview-page">
      {/* Loading Overlay */}
      {(loading || globalDataLoading || !hasDataLoaded) && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-progress-container">
              <div className="loading-progress-bar" style={{ width: `${adjustedLoadingProgress}%` }}></div>
            </div>
            <div className="loading-text">Loading Data</div>
            <div className="loading-percentage">{Math.round(adjustedLoadingProgress)}%</div>
          </div>
        </div>
      )}

      {/* Main Content - Only show when not loading */}
      {!loading && !globalDataLoading && hasDataLoaded && (
        <>
          {/* Summary Cards Row */}
          <div className="summary-cards-row">
        <div className="summary-card">
          <h3 className="summary-card-title">DAT Tickers Tracked</h3>
          <p className="summary-card-description">Distinct ticker symbols for public companies holding digital assets</p>
          <p className="summary-card-value">{getTotalTickers()}</p>
          <button className="submit-ticker-btn" onClick={openModal}>Submit Missing Ticker</button>
        </div>

        <div className="summary-card">
          <h3 className="summary-card-title">Total DAT Market Cap</h3>
          <p className="summary-card-description">Combined market value of tracked companies</p>
          <p className="summary-card-value">{formatMarketCap(totalMarketCap)}</p>
        </div>

        <div className="summary-card">
          <h3 className="summary-card-title">Onchain Transparency Index</h3>
          <p className="summary-card-description">Percentage of distinct tickers with disclosed wallets</p>
          <p className="summary-card-value">{getOnchainTransparencyPercentage()}%</p>
          <p className="summary-card-subtitle">{getDisclosedTickersCount()} Tickers</p>
        </div>
      </div>

      {/* Leaderboard Section */}
      <div className="leaderboard-section">
        <div className="chart-title-section">
          <h3 className="chart-title">DAT Company Leaderboard</h3>
          <p className="chart-subtitle">Category Leaders: DAT & Non-DAT Companies (updated daily)</p>
        </div>
        
        <div className="leaderboard-container">
        <div className="leaderboard-header-row">
          <div className="header-column">Category</div>
          <div className="header-column" style={{visibility: 'hidden'}}>Temp</div>
          <div className="header-column">DAT Company</div>
          <div className="header-column" style={{visibility: 'hidden'}}>Runner Up</div>
          <div className="header-column">Metric</div>
        </div>
          <div className="leaderboard-item">
            <div className="category-header">
              <div>
                <div className="category-title">Highest Price Growth</div>
                <div className="category-subtitle">Compared to all listed Equities</div>
              </div>
            </div>
            <div className="temp-column">
              <div className="temp-ticker">
                {datLeaders?.priceChangeTemp 
                  ? datLeaders.priceChangeTemp.ticker
                  : 'N/A'}
              </div>
              <div className="temp-rank">
                {datLeaders?.priceChangeLeader 
                  ? `Rank #${datLeaders.priceChangeLeader.rank_change_perc - 1}`
                  : 'N/A'}
              </div>
            </div>
            <div className="leader-info">
              <div className="leader-company">
                {datLeaders?.priceChangeLeader 
                  ? getCompanyName(datLeaders.priceChangeLeader.ticker)
                  : loading ? 'Loading...' : 'N/A'}
              </div>
              <div className="leader-ticker">
                {datLeaders?.priceChangeLeader 
                  ? datLeaders.priceChangeLeader.ticker
                  : 'N/A'}
              </div>
            </div>
            <div className="runner-up">
              <div className="runner-up-ticker">
                {datLeaders?.priceChangeRunnerUp 
                  ? datLeaders.priceChangeRunnerUp.ticker
                  : 'N/A'}
              </div>
              <div className="runner-up-rank">
                {datLeaders?.priceChangeRunnerUp 
                  ? `Rank #${datLeaders.priceChangeRunnerUp.rank_change_perc}`
                  : 'N/A'}
              </div>
            </div>
            <div className="numeric-stat">
              <div className="stat-value">
                {datLeaders?.priceChangeLeader 
                  ? `${datLeaders.priceChangeLeader.todays_change_perc?.toFixed(2) || 'N/A'}%`
                  : 'N/A'}
              </div>
              <div className="stat-label">
                {datLeaders?.priceChangeLeader 
                  ? `Rank #${datLeaders.priceChangeLeader.rank_change_perc}`
                  : 'N/A'}
              </div>
            </div>
          </div>

          <div className="leaderboard-item">
            <div className="category-header">
              <div>
                <div className="category-title">Most Traded by Transactions</div>
                <div className="category-subtitle">Compared to all listed Equities</div>
              </div>
            </div>
            <div className="temp-column">
              <div className="temp-ticker">
                {datLeaders?.transactionCountTemp 
                  ? datLeaders.transactionCountTemp.ticker
                  : 'N/A'}
              </div>
              <div className="temp-rank">
                {datLeaders?.transactionCountLeader 
                  ? `Rank #${datLeaders.transactionCountLeader.rank_transactions - 1}`
                  : 'N/A'}
              </div>
            </div>
            <div className="leader-info">
              <div className="leader-company">
                {datLeaders?.transactionCountLeader 
                  ? getCompanyName(datLeaders.transactionCountLeader.ticker)
                  : loading ? 'Loading...' : 'N/A'}
              </div>
              <div className="leader-ticker">
                {datLeaders?.transactionCountLeader 
                  ? datLeaders.transactionCountLeader.ticker
                  : 'N/A'}
              </div>
            </div>
            <div className="runner-up">
              <div className="runner-up-ticker">
                {datLeaders?.transactionCountRunnerUp 
                  ? datLeaders.transactionCountRunnerUp.ticker
                  : 'N/A'}
              </div>
              <div className="runner-up-rank">
                {datLeaders?.transactionCountRunnerUp 
                  ? `Rank #${datLeaders.transactionCountRunnerUp.rank_transactions}`
                  : 'N/A'}
              </div>
            </div>
            <div className="numeric-stat">
              <div className="stat-value">
                {datLeaders?.transactionCountLeader 
                  ? formatNumber(datLeaders.transactionCountLeader.transactions)
                  : 'N/A'}
              </div>
              <div className="stat-label">
                {datLeaders?.transactionCountLeader 
                  ? `Rank #${datLeaders.transactionCountLeader.rank_transactions}`
                  : 'N/A'}
              </div>
            </div>
          </div>

          <div className="leaderboard-item">
            <div className="category-header">
              <div>
                <div className="category-title">Highest Trading Volume</div>
                <div className="category-subtitle">Compared to all listed Equities</div>
              </div>
            </div>
            <div className="temp-column">
              <div className="temp-ticker">
                {datLeaders?.tradingVolumeTemp 
                  ? datLeaders.tradingVolumeTemp.ticker
                  : 'N/A'}
              </div>
              <div className="temp-rank">
                {datLeaders?.tradingVolumeLeader 
                  ? `Rank #${datLeaders.tradingVolumeLeader.rank_dollar_volume - 1}`
                  : 'N/A'}
              </div>
            </div>
            <div className="leader-info">
              <div className="leader-company">
                {datLeaders?.tradingVolumeLeader 
                  ? getCompanyName(datLeaders.tradingVolumeLeader.ticker)
                  : loading ? 'Loading...' : 'N/A'}
              </div>
              <div className="leader-ticker">
                {datLeaders?.tradingVolumeLeader 
                  ? datLeaders.tradingVolumeLeader.ticker
                  : 'N/A'}
              </div>
            </div>
            <div className="runner-up">
              <div className="runner-up-ticker">
                {datLeaders?.tradingVolumeRunnerUp 
                  ? datLeaders.tradingVolumeRunnerUp.ticker
                  : 'N/A'}
              </div>
              <div className="runner-up-rank">
                {datLeaders?.tradingVolumeRunnerUp 
                  ? `Rank #${datLeaders.tradingVolumeRunnerUp.rank_dollar_volume}`
                  : 'N/A'}
              </div>
            </div>
            <div className="numeric-stat">
              <div className="stat-value">
                {datLeaders?.tradingVolumeLeader 
                  ? (() => {
                      const value = datLeaders.tradingVolumeLeader.dollar_volume;
                      if (!value || value === 0) return 'N/A';
                      if (value >= 1e12) {
                        return '$' + (value / 1e12).toFixed(1) + 'T';
                      } else if (value >= 1e9) {
                        return '$' + (value / 1e9).toFixed(1) + 'B';
                      } else if (value >= 1e6) {
                        return '$' + (value / 1e6).toFixed(1) + 'M';
                      }
                      return '$' + value.toLocaleString();
                    })()
                  : 'N/A'}
              </div>
              <div className="stat-label">
                {datLeaders?.tradingVolumeLeader 
                  ? `Rank #${datLeaders.tradingVolumeLeader.rank_dollar_volume}`
                  : 'N/A'}
              </div>
            </div>
          </div>

          <div className="leaderboard-item">
            <div className="category-header">
              <div>
                <div className="category-title">Highest Market Cap</div>
                <div className="category-subtitle">Compared to other DATs</div>
              </div>
            </div>
            <div className="temp-column">
              <div className="temp-ticker">
                {(() => {
                  const marketCapData = getMarketCapLeaderboard();
                  return marketCapData && marketCapData.length > 1 
                    ? marketCapData[1].ticker
                    : '-';
                })()}
              </div>
              <div className="temp-rank">2ND</div>
            </div>
            <div className="leader-info">
              <div className="leader-company">
                {(() => {
                  const marketCapData = getMarketCapLeaderboard();
                  return marketCapData && marketCapData.length > 0 
                    ? getCompanyName(marketCapData[0].ticker)
                    : globalDataLoading ? 'Loading...' : 'N/A';
                })()}
              </div>
              <div className="leader-ticker">
                {(() => {
                  const marketCapData = getMarketCapLeaderboard();
                  return marketCapData && marketCapData.length > 0 
                    ? marketCapData[0].ticker
                    : 'N/A';
                })()}
              </div>
            </div>
            <div className="runner-up">
              <div className="runner-up-ticker">
                {(() => {
                  const marketCapData = getMarketCapLeaderboard();
                  return marketCapData && marketCapData.length > 2 
                    ? marketCapData[2].ticker
                    : '-';
                })()}
              </div>
              <div className="runner-up-rank">3RD</div>
            </div>
            <div className="numeric-stat">
              <div className="stat-value">
                {(() => {
                  const marketCapData = getMarketCapLeaderboard();
                  return marketCapData && marketCapData.length > 0 
                    ? formatUSD(marketCapData[0].market_cap)
                    : 'N/A';
                })()}
              </div>
              <div className="stat-label">Current</div>
            </div>
          </div>

          <div className="leaderboard-item">
            <div className="category-header">
              <div>
                <div className="category-title">Highest Premium</div>
                <div className="category-subtitle">Compared to other DATs</div>
              </div>
            </div>
            <div className="temp-column">
              <div className="temp-ticker">
                {(() => {
                  const premiumData = getPremiumLeaderboard();
                  return premiumData && premiumData.length > 1 
                    ? premiumData[1].ticker
                    : '-';
                })()}
              </div>
              <div className="temp-rank">2ND</div>
            </div>
            <div className="leader-info">
              <div className="leader-company">
                {(() => {
                  const premiumData = getPremiumLeaderboard();
                  return premiumData && premiumData.length > 0 
                    ? getCompanyName(premiumData[0].ticker)
                    : globalDataLoading ? 'Loading...' : 'N/A';
                })()}
              </div>
              <div className="leader-ticker">
                {(() => {
                  const premiumData = getPremiumLeaderboard();
                  return premiumData && premiumData.length > 0 
                    ? premiumData[0].ticker
                    : 'N/A';
                })()}
              </div>
            </div>
            <div className="runner-up">
              <div className="runner-up-ticker">
                {(() => {
                  const premiumData = getPremiumLeaderboard();
                  return premiumData && premiumData.length > 2 
                    ? premiumData[2].ticker
                    : '-';
                })()}
              </div>
              <div className="runner-up-rank">3RD</div>
            </div>
            <div className="numeric-stat">
              <div className="stat-value">
                {(() => {
                  const premiumData = getPremiumLeaderboard();
                  return premiumData && premiumData.length > 0 && premiumData[0].premium_discount !== null && premiumData[0].premium_discount !== undefined
                    ? `${(premiumData[0].premium_discount * 100).toFixed(2)}%`
                    : 'N/A';
                })()}
              </div>
              <div className="stat-label">Premium</div>
            </div>
          </div>
        </div>
      </div>

      {/* Latest Day Data Table */}
      <div className="latest-day-table-section">
        <div className="chart-title-section">
          <h3 className="chart-title">Latest Day Data</h3>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', position: 'relative' }}>
            <p className="chart-subtitle" style={{ margin: '0 auto', textAlign: 'center', flex: 1 }}>
            {latestSnapshotData && latestSnapshotData.length > 0 
              ? (() => {
                  // Find BMNR record to get the series_date
                  const bmnrRecord = latestSnapshotData.find(d => d.ticker === 'BMNR');
                  const dataDate = bmnrRecord?.series_date;
                  if (dataDate) {
                    // Extract just the date portion (YYYY-MM-DD) from the ISO string to avoid timezone issues
                    const dateMatch = dataDate.match(/(\d{4})-(\d{2})-(\d{2})/);
                    if (dateMatch) {
                      const year = parseInt(dateMatch[1], 10);
                      const month = parseInt(dateMatch[2], 10) - 1; // months are 0-indexed
                      const day = parseInt(dateMatch[3], 10);
                      const date = new Date(year, month, day);
                      const formatted = date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
                      return `Data as of ${formatted}`;
                    }
                  }
                  return 'Complete daily snapshot data for all tickers';
                })()
              : 'Complete daily snapshot data for all tickers'
            }
            </p>
            {latestSnapshotData && latestSnapshotData.length > 0 && (
              <button 
                onClick={downloadLatestDataAsCSV}
                style={{
                  padding: '8px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '24px',
                  color: '#22c55e',
                  transition: 'transform 0.2s',
                  position: 'absolute',
                  right: 0
                }}
                onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                title="Download CSV"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
              </button>
            )}
          </div>
        </div>
        
        {loading ? (
          <div className="table-loading">
            <div className="loading-spinner"></div>
            <p>Loading latest day data...</p>
          </div>
        ) : latestSnapshotData && latestSnapshotData.length > 0 ? (
          <div className="data-table-container">
            <div className="data-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="sortable-header" style={{ textAlign: 'left' }} onClick={() => handleSort('ticker')}>
                      Ticker {getSortIcon('ticker')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('mcap_estimated')}>
                      Market Cap {getSortIcon('mcap_estimated')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('crypto_holdings')}>
                      Crypto Holdings {getSortIcon('crypto_holdings')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('asset_tracked')}>
                      Asset Tracked {getSortIcon('asset_tracked')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('crypto_holdings_usd')}>
                      Holdings (USD) {getSortIcon('crypto_holdings_usd')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('stock_price_close')}>
                      Stock Price Close {getSortIcon('stock_price_close')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('trading_tx_count')}>
                      TXN Count {getSortIcon('trading_tx_count')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('nav_per_share')}>
                      NAV Per Share {getSortIcon('nav_per_share')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('nav_usd')}>
                      NAV USD {getSortIcon('nav_usd')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('crypto_per_share')}>
                      Crypto Per Share {getSortIcon('crypto_per_share')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('outstanding_shares_estimated')}>
                      Outstanding Shares {getSortIcon('outstanding_shares_estimated')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('mnav')}>
                      mNAV {getSortIcon('mnav')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('premium_discount')}>
                      Premium or Discount {getSortIcon('premium_discount')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {getSortableData(latestSnapshotData).map((row, index) => (
                    <tr key={`${row.ticker}-${row.date}-${index}`}>
                      <td className="ticker-cell">{row.ticker}</td>
                      <td className="number-cell">{row.mcap_estimated ? formatUSD(row.mcap_estimated) : 'N/A'}</td>
                      <td className="number-cell">{row.crypto_holdings ? formatCryptoHoldings(row.crypto_holdings) : 'N/A'}</td>
                      <td className="asset-tracked-cell">{getAssetTracked(row.ticker)}</td>
                      <td className="number-cell">{row.crypto_holdings_usd ? formatUSD(row.crypto_holdings_usd) : 'N/A'}</td>
                      <td className="number-cell">{row.stock_price_close ? `$${parseFloat(row.stock_price_close).toFixed(2)}` : 'N/A'}</td>
                      <td className="number-cell">{row.trading_tx_count ? formatNumber(row.trading_tx_count) : 'N/A'}</td>
                      <td className="number-cell">{row.nav_per_share ? `$${parseFloat(row.nav_per_share).toFixed(2)}` : 'N/A'}</td>
                      <td className="number-cell">{row.nav_usd ? formatUSD(row.nav_usd) : 'N/A'}</td>
                      <td className="number-cell">{row.crypto_per_share ? parseFloat(row.crypto_per_share).toFixed(6) : 'N/A'}</td>
                      <td className="number-cell">{row.outstanding_shares_estimated ? formatNumber(row.outstanding_shares_estimated) : 'N/A'}</td>
                      <td className="number-cell">{row.mnav ? formatNumber(row.mnav) : 'N/A'}</td>
                      <td className="number-cell">{row.premium_discount !== null && row.premium_discount !== undefined ? `${(row.premium_discount * 100).toFixed(2)}%` : 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="no-data-message">
            <p>No latest day data available</p>
          </div>
        )}
      </div>

      {/* Analytics Section */}
      <div className="analytics-section">
        <div className="charts-container">
          <div className="chart-title-section">
            <h3 className="chart-title">Treasury Company Composition</h3>
            <p className="chart-subtitle">Visual breakdown of crypto holdings by asset type</p>
          </div>
          <div className="chart-and-cards">
            <div className="data-cards left-cards">
              <div className="data-card eth-card" onClick={() => handleAssetCardClick('ethereum')} style={{ cursor: 'pointer' }}>
                <div className="card-header">
                  <h4 className="card-title">ETH</h4>
                  <span className="card-percentage">{cryptoHoldings?.ethereum?.percentage?.toFixed(1) || 15.2}%</span>
                </div>
                <div className="card-content">
                  <div className="ticker-list">
                    {getTopTickersWithOther('ethereum').map((item, index) => (
                      <div 
                        key={index}
                        className="ticker-item" 
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          if (!item.isOther) {
                            handleTickerClick(item.ticker); 
                          }
                        }} 
                        style={{ cursor: item.isOther ? 'default' : 'pointer' }}
                      >
                        <span className="ticker-name">{item.ticker}</span>
                        <span className="ticker-share">{item.percentage.toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="data-card sol-card" onClick={() => handleAssetCardClick('solana')} style={{ cursor: 'pointer' }}>
                <div className="card-header">
                  <h4 className="card-title">SOL</h4>
                  <span className="card-percentage">{cryptoHoldings?.solana?.percentage?.toFixed(1) || 12.3}%</span>
                </div>
                <div className="card-content">
                  <div className="ticker-list">
                    {getTopTickersWithOther('solana').map((item, index) => (
                      <div 
                        key={index}
                        className="ticker-item" 
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          if (!item.isOther) {
                            handleTickerClick(item.ticker); 
                          }
                        }} 
                        style={{ cursor: item.isOther ? 'default' : 'pointer' }}
                      >
                        <span className="ticker-name">{item.ticker}</span>
                        <span className="ticker-share">{item.percentage.toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <PieChart data={cryptoHoldings} />
            <div className="data-cards right-cards">
              <div className="data-card btc-card" onClick={() => handleAssetCardClick('bitcoin')} style={{ cursor: 'pointer' }}>
                <div className="card-header">
                  <h4 className="card-title">BTC</h4>
                  <span className="card-percentage">{cryptoHoldings?.bitcoin?.percentage?.toFixed(1) || 72.5}%</span>
                </div>
                <div className="card-content">
                  <div className="ticker-list">
                    {getTopTickersWithOther('bitcoin').map((item, index) => (
                      <div 
                        key={index}
                        className="ticker-item" 
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          if (!item.isOther) {
                            handleTickerClick(item.ticker); 
                          }
                        }} 
                        style={{ cursor: item.isOther ? 'default' : 'pointer' }}
                      >
                        <span className="ticker-name">{item.ticker}</span>
                        <span className="ticker-share">{item.percentage.toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Time Series Charts Section */}
      <div className="time-series-section">
        <div className="chart-title-section">
          <h3 className="chart-title">Time Series Analytics</h3>
          <p className="chart-subtitle">Historical trends and market dynamics over time</p>
        </div>
        
        <div className="charts-container">
          <div className="chart-container" onClick={() => !isMobile && setModalChart('nav_growth')}>
            <div className="chart-logo-wrapper">
              <ChartLogo />
            </div>
            <StackedAreaChart
              data={getNAVGrowthDataByAsset()}
              xKey="date"
              yKeys={['btc_nav_growth', 'eth_nav_growth', 'sol_nav_growth']}
              title="NAV Growth Over Time"
              colors={['#fbbf24', '#3b82f6', '#8b5cf6']}
              formatCurrency={false}
              xLabel="Date"
              yLabel="NAV Growth"
              legendLabels={['BTC', 'ETH', 'SOL']}
            />
          </div>

          <div className="chart-container" onClick={() => !isMobile && setModalChart('supply_percentage')}>
            <div className="chart-logo-wrapper">
              <ChartLogo />
            </div>
            <LineChartWithLegend
              data={timeSeriesData}
              xKey="date"
              yKeys={['btc_supply', 'eth_supply', 'sol_supply']}
              title="Share of Circulating Supply by Asset"
              colors={['#fbbf24', '#3b82f6', '#8b5cf6']}
              xLabel="Date"
              yLabel="Supply %"
              legendLabels={['BTC', 'ETH', 'SOL']}
            />
          </div>

          <div className="chart-container" onClick={() => !isMobile && setModalChart('trading_volume')}>
            <div className="chart-logo-wrapper">
              <ChartLogo />
            </div>
            <StackedAreaChart
              data={getTimeSeriesByAsset() || timeSeriesData}
              xKey="date"
              yKeys={['btc_volume', 'eth_volume', 'sol_volume']}
              title="Equity Market Share by Trading Volume"
              colors={['#fbbf24', '#3b82f6', '#8b5cf6']}
              xLabel="Date"
              yLabel="Volume %"
              legendLabels={['BTC', 'ETH', 'SOL']}
            />
          </div>

          <div className="chart-container" onClick={() => !isMobile && setModalChart('market_cap')}>
            <div className="chart-logo-wrapper">
              <ChartLogo />
            </div>
            <StackedAreaChart
              data={getTimeSeriesByAsset() || timeSeriesData}
              xKey="date"
              yKeys={['btc_mcap', 'eth_mcap', 'sol_mcap']}
              title="Aggregate Market Cap of DAT Companies"
              colors={['#fbbf24', '#3b82f6', '#8b5cf6']}
              formatCurrency={true}
              xLabel="Date"
              yLabel="Market Cap ($)"
              legendLabels={['BTC', 'ETH', 'SOL']}
            />
          </div>
        </div>
      </div>
        </>
      )}

      {/* Chart Modals */}
      <ChartModal isOpen={modalChart === 'nav_growth'} onClose={() => setModalChart(null)}>
        <StackedAreaChart
          data={getNAVGrowthDataByAsset()}
          xKey="date"
          yKeys={['btc_nav_growth', 'eth_nav_growth', 'sol_nav_growth']}
          title="NAV Growth Over Time"
          colors={['#fbbf24', '#3b82f6', '#8b5cf6']}
          formatCurrency={false}
          xLabel="Date"
          yLabel="NAV Growth"
          fullHeight={true}
          legendLabels={['BTC', 'ETH', 'SOL']}
        />
      </ChartModal>

      <ChartModal isOpen={modalChart === 'supply_percentage'} onClose={() => setModalChart(null)}>
        <LineChartWithLegend
          data={timeSeriesData}
          xKey="date"
          yKeys={['btc_supply', 'eth_supply', 'sol_supply']}
          title="Share of Circulating Supply by Asset"
          colors={['#fbbf24', '#3b82f6', '#8b5cf6']}
          xLabel="Date"
          yLabel="Supply %"
          fullHeight={true}
          legendLabels={['BTC', 'ETH', 'SOL']}
        />
      </ChartModal>

      <ChartModal isOpen={modalChart === 'trading_volume'} onClose={() => setModalChart(null)}>
        <StackedAreaChart
          data={getTimeSeriesByAsset() || timeSeriesData}
          xKey="date"
          yKeys={['btc_volume', 'eth_volume', 'sol_volume']}
          title="Equity Market Share by Trading Volume"
          colors={['#fbbf24', '#3b82f6', '#8b5cf6']}
          xLabel="Date"
          yLabel="Volume %"
          fullHeight={true}
          legendLabels={['BTC', 'ETH', 'SOL']}
        />
      </ChartModal>

      <ChartModal isOpen={modalChart === 'market_cap'} onClose={() => setModalChart(null)}>
        <StackedAreaChart
          data={getTimeSeriesByAsset() || timeSeriesData}
          xKey="date"
          yKeys={['btc_mcap', 'eth_mcap', 'sol_mcap']}
          title="Aggregate Market Cap of DAT Companies"
          colors={['#fbbf24', '#3b82f6', '#8b5cf6']}
          formatCurrency={true}
          xLabel="Date"
          yLabel="Market Cap ($)"
          fullHeight={true}
          legendLabels={['BTC', 'ETH', 'SOL']}
        />
      </ChartModal>

      {/* Modal Overlay */}
      {tickerModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Submit Missing Ticker</h3>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <form onSubmit={handleSubmitTicker} className="modal-form">
              <div className="form-group">
                <label htmlFor="ticker">Ticker Symbol</label>
                <input
                  type="text"
                  id="ticker"
                  value={tickerInput}
                  onChange={(e) => {
                    setTickerInput(e.target.value);
                    setTickerSubmitError(null);
                  }}
                  placeholder="e.g., AAPL, TSLA, NVDA"
                  required
                  autoFocus
                  disabled={tickerSubmitting}
                />
                {tickerSubmitError && (
                  <div className="form-error" style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                    {tickerSubmitError}
                  </div>
                )}
              </div>
              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={closeModal} disabled={tickerSubmitting}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={tickerSubmitting || !tickerInput.trim()}>
                  {tickerSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OverviewPage;

